import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GuestOverrideProvider } from 'contexts/GuestOverrideContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useFeatureFlag, useFeatureFlagExposure } from 'featureFlags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { deriveStaleAfterMs } from 'hooks/useActivityStripFeed';
import { useGuest } from 'hooks/useGuest';
import {
  findRegisteredTournaments,
  pickAboutToStartCandidate,
  resolveAboutToStartJoinTarget
} from 'pages/Lobby/tournamentAboutToStart';
import { requestActivityStripRetry } from 'reducers/activityStrip';
import {
  selectRestrictedMttIds,
  selectRestrictedOmahaCGIds,
  selectRestrictedSngIds,
  selectRestrictedTexasCGIds
} from 'reducers/colluderRestrictions.selectors';
import { RecentTable } from 'reducers/user';
import { clearTournamentIntent, peekTournamentIntent } from 'utils/auth';
import { getLobbyAssetUrl, retainImage } from 'utils/media';

import {
  applyHighTraffic,
  isDemoTableId,
  padCashGames,
  projectSitGos,
  projectTournaments
} from 'components/LobbyLab/demoTables';
import { LAB_MAINTENANCE_NOTICE, useLobbyLab } from 'components/LobbyLab/lobbyLabState';
import TournamentAboutToStartModal from 'components/TournamentAboutToStartModal';
import TournamentDetailModal from 'components/TournamentDetailModal';
import PayoutPreload from 'components/TournamentPayouts/PayoutPreload';

import { ActivityStripStatus } from './ActivityStrip';
import { clearLobbyLanding, peekLobbyLanding } from './lobbyLanding';
import { localDayKey, projectRecurringTournaments } from './recurringSchedule';
import SaloonLobby, { Format } from './SaloonLobby';

// Lobby Lab's NEW badge preview (EN-10748): one stable array, so the lobby's
// memo of the badge set is not rebuilt on every container render.
const SITGO_NEW_PREVIEW: readonly Format[] = ['sitgo'];
import { isShutdownPending } from './shutdownState';
import { detectSitgoVariant } from './sitgoVariant';
import { formatStakesLabel } from './stakesLabel';
import {
  ActivityMetrics,
  CashGame,
  DayMeta,
  PlayerSummary,
  PromoData,
  SitGo,
  Tournament
} from './types';
import { useActiveTables } from './useActiveTables';

const cashBannerFor = (variant: CashGame['variant']): string => {
  if (variant === 'PLO') return 'cash-plo-v1.png';
  if (variant === 'PLO-HiLo') return 'cash-plo-hilo-v1.png';
  return 'cash-nlh-v1.png';
};

const sitgoBannerFor = (variant: SitGo['variant']): string =>
  variant === 'PLO' ? 'sitgo-plo-v1.png' : 'sitgo-nlh-v1.png';

// Legacy engine lobby rows ship two type fields per table:
//   • `gameType`            — code: 'NL' / 'PL' / 'HiLoNL' / 'HiLoPL'
//   • `gameTypeDisplayName` — display: 'NLH' / 'NL Omaha' / 'PLO' /
//     'NL Omaha HiLo' / 'PLO HiLo' (see engine games/common/constants.js
//     OMAHA_GAME_TYPE + TEXAS_GAME_TYPE).
// The previous implementation read `raw.game` / `raw.tablegame` — fields
// that don't exist on cash lobby rows — so every row fell through to the
// 'NLH' default. Hi/Lo tables in particular were getting bucketed with
// regular Omaha and rendered identically.
//
// Detection order matches the engine's own check (omaha/socket.js uses
// `gameType.includes(GAME_TYPE_SUBSTRING_HILO)` where the substring is
// the literal 'HiLo'): Hi/Lo wins over plain Omaha; Omaha wins over NLH.
const detectCashVariant = (raw: any): CashGame['variant'] => {
  const gameType = String(raw?.gameType ?? '');
  const displayName = String(raw?.gameTypeDisplayName ?? '').toLowerCase();
  // Hi/Lo: case-sensitive 'HiLo' on the code, fuzzy on the display name
  // (covers any future display strings that spell it as "Hi/Lo" or "Hi-Lo").
  if (
    gameType.includes('HiLo') ||
    displayName.includes('hilo') ||
    displayName.includes('hi/lo') ||
    displayName.includes('hi-lo')
  ) {
    return 'PLO-HiLo';
  }
  // EN-10379: this MUST test gameType, not only displayName.
  //
  // The omaha codes the admin template form writes are 'PLO', 'NLO',
  // 'PLO HiLo', 'NLO HiLo'. Tables created by the table-manager reseed carry
  // gameType straight from the template and have NO gameTypeDisplayName — and
  // lobby-broadcast's LOBBY_COLUMNS does not select that column either, so it is
  // absent from the payload regardless. Anchoring on the display name therefore
  // sent every reseed-created PLO table down to 'NLH'.
  //
  // The Hi/Lo branch above already tests gameType, which is exactly why Hi/Lo
  // rendered correctly while plain PLO did not.
  if (
    /^(PLO|NLO)\b/i.test(gameType) ||
    displayName.includes('omaha') ||
    displayName.includes('plo')
  ) {
    return 'PLO';
  }
  return 'NLH';
};

// Mirrors Unity's LobbiesModel classification: a tournament's category is
// driven by the `gameType` string (plus the `isSatellite` flag and a price
// check for Freeroll). Categories overlap — a Bounty can also be a Freeroll
// if entry+buy-in is zero — so we check in priority order.
const detectTournamentType = (raw: any): Tournament['type'] => {
  const gt = String(raw?.gameType || '').toLowerCase();
  const entryFee = Number(raw?.entryFee ?? 0);
  const buyIn = Number(raw?.buyIn ?? 0);

  // Satellite: gameType value OR explicit flag (matches Unity's dual check).
  if (gt === 'satellite' || raw?.isSatellite === 1 || String(raw?.isSatellite) === '1') {
    return 'Satellite';
  }
  if (gt === 'bounty' && Number(raw?.bountyAmount ?? 0) > 0) return 'Bounty';
  if (gt === 'freezeout' || gt === 'freezout') return 'Freezeout';
  if (gt === 'freeroll' || entryFee + buyIn === 0) return 'Freeroll';
  // Unity lumps Standard + Re-Entry under one chip ('NLH'). For our UI
  // we surface them both as 'Re-Entry' — that's how the user thinks of them.
  return 'Re-Entry';
};

// A tournament allows re-entry when its structure says so (gameType) or the
// engine reports a nonzero / unlimited rebuy allowance (`rebuys` is 'Unlimited'
// or -1 for unlimited, a positive count otherwise). Used to label re-entry
// freerolls as "Re-entry" rather than plain "Freeroll".
const allowsReentry = (raw: any): boolean => {
  const gt = String(raw?.gameType || raw?.tournamentType || raw?.type || '').toLowerCase();
  if (gt.includes('re-entry') || gt.includes('reentry') || gt.includes('re entry')) return true;
  const rebuys = raw?.rebuys;
  if (String(rebuys).toLowerCase() === 'unlimited') return true;
  const n = Number(rebuys);
  return Number.isFinite(n) && (n === -1 || n > 0);
};

const detectTournamentStatus = (raw: any): 'A' | 'R' | 'P' | 'F' | 'C' => {
  const s = String(raw?.status || 'A').toUpperCase();
  if (s === 'R' || s === 'P' || s === 'F' || s === 'C') return s;
  return 'A';
};

// Day-of-week check in America/Chicago. The operator is on Central time, so
// "Sunday" for marketing purposes is Sunday CST/CDT regardless of where the
// player's browser is. Intl handles DST automatically (CDT in summer,
// CST in winter) — no manual offset math needed.
const isSundayCST = (startAtSeconds: number): boolean => {
  if (!startAtSeconds) return false;
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    weekday: 'short'
  }).format(new Date(startAtSeconds * 1000));
  return weekday === 'Sun';
};

// Banner-art picker for tournament tiles.
//
// Every freeroll banner has its dollar amount PAINTED INTO the artwork
// ("$500 MORNING FREEROLL", "$1000 DAILY FREEROLL", "$5000 GTD FREEROLL"), so
// the art is chosen by the tournament's guarantee. Keyed on `guarantee` only,
// NOT on the title, and NOT on `prizePool`, which the recurrence rollover
// resets to 0 on every occurrence.
//
// Adding a new freeroll amount is: drop the 320x320 PNG in
// apps/assets/images/lobby, upload it to media.hijackpoker.com/images/lobby,
// and add one line here.
const FREEROLL_BANNER_BY_GUARANTEE: Record<number, string> = {
  500: 'banner-morning-freeroll-v1.png',
  1000: 'banner-daily-freeroll-v1.png',
  5000: 'banner-5k-freeroll-v1.png',
  9500: 'banner-9-to-5-freeroll-v1.png'
};

// Carries no dollar figure, so it is safe for any amount we have no art for.
// A freeroll with an unrecognized guarantee lands here rather than on art
// advertising a number the tournament does not pay.
const FALLBACK_BANNER = 'banner-nlh-v1.png';

// Rules (per product, May 22 2026):
//   • Sunday Funday  → Sunday in CST + 'Funday' in title + guarantee == $25,000
//   • Money Train    → 'Moneytrain' in title + Freeroll + guarantee == $5,000
//   • Freeroll       → guarantee, via FREEROLL_BANNER_BY_GUARANTEE
//   • 10K Everyday   → '10k Everyday' in title
//   • Midnight Madness → 'Midnight Madness Turbo' in title
//   • High Noon      → 'High Noon' in title
//   • Big Bounty     → 'Big Bounty' in title
//   • Type fallbacks → Satellite / Bounty / Freezeout banners
//   • Default        → NLH
//
// Rules are checked most-specific-first so a Sunday Funday $25K freeroll
// gets the dedicated banner instead of falling through to a generic
// freeroll banner.
export const tournamentBannerFor = (
  type: Tournament['type'],
  title: string,
  guarantee: number,
  startAtSeconds: number
): string => {
  const t = (title || '').toLowerCase();
  const isFreeroll = type === 'Freeroll';

  // Compound rules (title + freeroll + guarantee combinations). These two stay
  // AHEAD of the guarantee map because their art carries its own amount
  // ($25,000 Sunday Funday, $5K MoneyTrain), so a $5,000 MoneyTrain must keep
  // its branded banner instead of picking up the generic $5000 freeroll art.
  if (isSundayCST(startAtSeconds) && t.includes('funday')) {
    return 'sunday-funday-banner-v1.png';
  }
  if (t.includes('moneytrain') && isFreeroll) {
    return 'banner-money-train-v1.png';
  }
  // Freeroll art is amount-specific, so it is keyed on the guarantee and never
  // on the title. Title-keying is what put "$1000 DAILY FREEROLL" art on the
  // $5,000 afternoon freeroll.
  if (isFreeroll) return FREEROLL_BANNER_BY_GUARANTEE[guarantee] ?? FALLBACK_BANNER;

  // Title-keyed specials
  if (t.includes('10k everyday')) return 'banner-10k-everyday-v1.png';
  if (t.includes('midnight madness turbo')) return 'banner-midnight-madness-v1.png';
  if (t.includes('high noon')) return 'banner-high-noon-v1.png';
  if (t.includes('big bounty')) return 'banner-big-bounty-v1.png';

  // Type fallbacks (Satellite no longer takes priority over the named-event
  // rules above — none of the named-event rules can match a Satellite
  // tournament anyway, so ordering doesn't matter in practice).
  if (type === 'Satellite') return 'banner-satellite-v1.png';
  if (type === 'Bounty') return 'banner-bounty-v1.png';
  if (type === 'Freezeout') return 'banner-freezeout-v1.png';

  return FALLBACK_BANNER;
};

// Kick off fetches for every known lobby image as soon as this module loads,
// so tiles have their art by the time the player reaches the lobby. Eager
// MediaImages also retain whatever they render, so a banner missing from
// this list still stays cached after its first appearance.
const LOBBY_BANNERS = [
  'hijack-wordmark-v1.png',
  'cash-nlh-v1.png',
  'cash-plo-v1.png',
  'cash-plo-hilo-v1.png',
  'sitgo-nlh-v1.png',
  'sitgo-plo-v1.png',
  'banner-nlh-v1.png',
  'banner-bounty-v1.png',
  'banner-freezeout-v1.png',
  'banner-satellite-v1.png',
  'banner-big-bounty-v1.png',
  'banner-high-noon-v1.png',
  'banner-midnight-madness-v1.png',
  'banner-10k-everyday-v1.png',
  'banner-daily-freeroll-v1.png',
  'banner-morning-freeroll-v1.png',
  'banner-5k-freeroll-v1.png',
  'banner-9-to-5-freeroll-v1.png',
  'banner-money-train-v1.png',
  'sunday-funday-banner-v1.png'
];
LOBBY_BANNERS.forEach((name) => retainImage(getLobbyAssetUrl(name)));

// Real backend fields like `tablelow` and `stakes` arrive as formatted
// currency strings ("$3.00") instead of raw numbers, so a plain Number()
// returns NaN. Strip any non-numeric chars before parsing.
export const parseMoney = (v: unknown): number => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const str = String(v).trim();
  const n = parseFloat(str.replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(n)) return 0;
  // Display fields (e.g. tablelimitDisplay) can arrive abbreviated — "2K",
  // "$1.5K", "3M". Scale by the trailing magnitude suffix so "2K" parses to
  // 2000 instead of 2 (which was clipping the lobby buy-in to "$2").
  const suffix = str.match(/([kmb])\s*$/i)?.[1]?.toLowerCase();
  const multiplier = suffix === 'b' ? 1e9 : suffix === 'm' ? 1e6 : suffix === 'k' ? 1e3 : 1;
  return n * multiplier;
};

// Exported for test (see adaptSitGo, same pattern). EN-10379 came down to the
// variant detection inside here, which had no direct coverage.
export const adaptCashGame = (raw: any, idx: number): CashGame => {
  const variant = detectCashVariant(raw);
  const seated = Number(raw?.seatsTakenDisplay ?? raw?.seatsTaken ?? 0);
  const capacity = Number(raw?.seats ?? raw?.maxPlayers ?? 9);
  const buyInMin = parseMoney(raw?.tablelowDisplay ?? raw?.tablelow ?? raw?.minBuyIn);
  const buyInMax = parseMoney(raw?.tablelimitDisplay ?? raw?.tablelimit ?? raw?.maxBuyIn);
  const sbRaw = raw?.sb;
  const bbRaw = raw?.bb;
  const hasBlinds = sbRaw !== undefined && sbRaw !== null && bbRaw !== undefined && bbRaw !== null;
  // Engine strings arrive as "$0.02/$0.05"; the lobby writes "$0.02 / $0.05".
  const stakes = formatStakesLabel(raw?.stakes || (hasBlinds ? `$${sbRaw}/$${bbRaw}` : 'Unknown'));
  const sb = parseMoney(sbRaw);
  const rawTableId = raw?.gameID ?? raw?.tablename;
  const numericTableId = Number(rawTableId);
  const tableId = Number.isFinite(numericTableId) ? numericTableId : idx;

  return {
    id: String(raw?.gameID ?? raw?.id ?? `cash-${idx}`),
    tableId,
    variant,
    banner: cashBannerFor(variant),
    stakes,
    sb,
    buyInMin,
    buyInMax,
    seated,
    capacity,
    game: raw?.game,
    isPrivate: raw?.privateTable === 1 || raw?.privateTable === true,
    waitlistCount: Number(raw?.waitlistCount ?? 0),
    pendingShutdown: isShutdownPending(raw)
  };
};

// EN-11139 AC 4: the server's Sit & Go template key (redisKeys
// sngTemplateKeySuffix) without the buy-in, which is the lobby section. The
// engine's lobby row carries the title as `tablename`.
const sitGoTemplateKey = (raw: any): string =>
  [
    raw?.tablename ?? raw?.title,
    raw?.game,
    raw?.minPlayers,
    raw?.maxPlayers ?? raw?.capacity,
    raw?.entryFee,
    raw?.schedule,
    raw?.startingChips
  ]
    .map((part) => encodeURIComponent(String(part ?? '')))
    .join('|');

// Exported for unit testing.
export const adaptSitGo = (raw: any, idx: number, registeredIds: Set<string>): SitGo => {
  const variant = detectSitgoVariant(raw);
  const id = String(raw?.tournamentId ?? raw?.id ?? `sitgo-${idx}`);
  const rawTableId = raw?.tournamentId ?? raw?.id ?? raw?.tablename;
  const numericTableId = Number(rawTableId);
  const tableId = Number.isFinite(numericTableId) ? numericTableId : idx;
  return {
    id,
    tableId,
    variant,
    banner: sitgoBannerFor(variant),
    label: variant === 'PLO' ? 'PLO' : "NLH HOLD'EM",
    // Tournament name/type from the engine (e.g. "SNG 3P FIFO"). Mirror the MTT
    // adapter's chain, which reads `tablename` as the descriptive name (the SnG
    // H5 row carries the name there, not a numeric table id). Empty when none.
    name: String(
      raw?.tournamentName ?? raw?.tablename ?? raw?.name ?? raw?.gameName ?? raw?.title ?? ''
    ),
    buyIn: parseMoney(raw?.buyIn ?? raw?.entry),
    prizePool: parseMoney(raw?.prizePool ?? raw?.prize),
    // The engine's gotTournamentH5 broadcast ships the registered count as
    // `seatsTaken` (raw count) and `registeredPlayers` (count + rebuys) — it
    // never sends `registered`/`playerCount`. Reading those non-existent
    // fields pinned every SnG tile to 0/N. Mirror adaptCashGame (seatsTaken)
    // and adaptTournaments (registeredPlayers); keep the legacy keys last as
    // defensive fallbacks.
    registered: Number(
      raw?.seatsTaken ?? raw?.registeredPlayers ?? raw?.registered ?? raw?.playerCount ?? 0
    ),
    capacity: Number(raw?.capacity ?? raw?.maxPlayers ?? 9),
    game: raw?.game,
    templateKey: sitGoTemplateKey(raw),
    isRegistered: registeredIds.has(id)
  };
};

/**
 * The Sit & Go rows the lobby lists: those still registering or in play.
 * EN-9678 drops a cancelled one ('C'), as Unity does. EN-11305 drops a
 * finished one ('F') at once, whether or not the player still has its table
 * open: the engine's list already leaves them out (status <> 'F'), and a row
 * that still carries 'F' must not read as a live game.
 *
 * Exported for unit testing.
 */
export const isListedSitGo = (raw: any): boolean => {
  const status = String(raw?.status || 'A').toUpperCase();
  return status !== 'C' && status !== 'F';
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatDayMeta = (date: Date): DayMeta => {
  const dayName = DAY_NAMES[date.getDay()];
  const dayDate = date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  const dayNum = String(date.getDate()).padStart(2, '0');
  return { dayKey: localDayKey(date), dayName, dayDate, dayNum };
};

export const adaptTournaments = (
  rawList: any[],
  registeredIds: Set<string>,
  // Engine clock (epoch seconds). Used as the date source instead of the
  // client clock when a tournament is missing its own startAtTime.
  serverTime: number
): { tournaments: Tournament[]; days: DayMeta[] } => {
  const dayMap = new Map<string, DayMeta>();
  // EN-9678: drop cancelled tournaments before rendering tiles so the lobby
  // matches Unity (cancelled events are surfaced via the separate cancellation
  // notification + refund flow, not as a tile in the schedule).
  const tournaments: Tournament[] = rawList
    .filter((raw) => detectTournamentStatus(raw) !== 'C')
    // EN-10752: a tournament with no `startAtTime` belongs to the engine's
    // today, so before the first server tick it has no day to land on. It
    // waits for the tick (this is memoised on `serverTime`) rather than being
    // dated by the device clock, which could invent a column of its own.
    .filter((raw) => Number(raw?.startAtTime || 0) > 0 || serverTime > 0)
    .map((raw, idx) => {
      const type = detectTournamentType(raw);
      const startAtTime = Number(raw?.startAtTime || 0);
      const startDate = new Date((startAtTime > 0 ? startAtTime : serverTime) * 1000);
      const day = formatDayMeta(startDate);
      if (!dayMap.has(day.dayKey)) dayMap.set(day.dayKey, day);
      const title =
        raw?.tournamentName || raw?.tablename || raw?.name || raw?.title || 'Tournament';
      const id = String(raw?.tournamentId ?? raw?.id ?? `t-${idx}`);
      // Display label — what the tile/modal renders as the type pill. Freeroll
      // is classified by price (entryFee + buyIn === 0). A freeroll that ALSO
      // allows re-entry surfaces the "Re-entry" label (players want to know the
      // format), matching the paid re-entry pill; a plain freeroll stays
      // "Freeroll". Non-freerolls fall back to the backend's label, then the
      // mapped enum. (`type` stays 'Freeroll' so freeroll filtering/banner art
      // is unaffected — only the display label changes.)
      const typeLabel =
        type === 'Freeroll'
          ? allowsReentry(raw)
            ? 'Re-entry'
            : 'Freeroll'
          : String(raw?.gameType || raw?.tournamentType || raw?.type || type);
      return {
        id,
        title,
        banner: tournamentBannerFor(type, title, Number(raw?.guarantee ?? 0), startAtTime),
        // Hour:minute only — the day-of-week chip rail above the tile rail
        // already conveys the date, so showing "May 21, 2:06 PM" inline on
        // each tile was redundant. Now reads like "2:06 PM".
        startTime: startDate.toLocaleString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        }),
        startAt: startAtTime,
        dayKey: day.dayKey,
        dayName: day.dayName,
        dayDate: day.dayDate,
        buyIn: parseMoney(raw?.buyIn ?? raw?.entry),
        prizePool: parseMoney(raw?.prizePool ?? raw?.guaranteedPrizePool),
        guarantee: Number(raw?.guarantee ?? 0),
        // Satellite-only: broadcast pre-computes the seat count and ships it
        // on `satelliteSeatValue` (overwriting the per-seat dollar value from
        // the DB). Use this for "N Seats GTD" — the raw `guarantee` field is
        // dollars and would render as e.g. "1000 Seats" if used directly.
        satelliteSeats: Number(raw?.satelliteSeatValue ?? 0),
        registered: Number(raw?.registeredPlayers ?? raw?.registered ?? raw?.playerCount ?? 0),
        capacity: Number(raw?.maxPlayers ?? raw?.capacity ?? 0),
        type,
        typeLabel,
        status: detectTournamentStatus(raw),
        isRegistered: registeredIds.has(id),
        isSatellite: type === 'Satellite' || raw?.isSatellite === 1
      };
    });

  // Calendar range, anchored on the engine's "today" — and ONLY the engine's
  // (JRam, 2026-09-14). Until the first server tick there is no "today" this
  // lobby trusts: the carousel already refuses to mark a today-column off the
  // device clock (`todayKey` stays undefined until `serverTime` arrives), and
  // the range that decides which columns EXIST must not guess either. It used
  // to fall back to `new Date()`, so a device a day fast put the start in the
  // future and today's tournaments carried a dayKey with no column to land in —
  // invisibly, since EN-10744 stopped rendering empty days, so the tab simply
  // looked like today had nothing on.
  //
  // With a clock:
  //   • start — the engine's today (no past glimpse), extended back only to
  //             cover a still-running tournament that began earlier
  //   • end   — the later of today and the LAST scheduled tournament date
  // Without one: exactly the days the engine's own data spans, first to last.
  // `adaptTournaments` is memoised on `serverTime`, so the clocked range takes
  // over the moment the tick lands.
  //
  // Either way the range is CONTINUOUS (internal gaps filled), so the carousel
  // can step through days by index. Whether a day with nothing on it RENDERS is
  // the carousel's decision, and since EN-10744 it does not.
  //
  // A tournament with no `startAtTime` lands on the engine's today, and is held
  // back until the first tick (EN-10752, filter above). Not decided here,
  // deliberately: which calendar the dayKeys themselves belong to
  // (`formatDayMeta` groups by the device's local day).
  const PAST_GLIMPSE_DAYS = 0;
  const keyToDate = (key: string) => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const presentKeys = Array.from(dayMap.keys()).sort((a, b) => a.localeCompare(b));

  let rangeStart: Date;
  let rangeEnd: Date;
  if (serverTime > 0) {
    const today = new Date(serverTime * 1000);
    rangeStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - PAST_GLIMPSE_DAYS
    );
    // Extend the start back to cover any still-running tournament (status 'P')
    // that began before today — a multi-day MTT must keep its column so it
    // stays visible in the schedule.
    const runningKeys = tournaments
      .filter((t) => t.status === 'P')
      .map((t) => t.dayKey)
      .sort((a, b) => a.localeCompare(b));
    if (runningKeys.length > 0) {
      const earliestRunning = keyToDate(runningKeys[0]);
      if (earliestRunning < rangeStart) rangeStart = earliestRunning;
    }
    rangeEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (presentKeys.length > 0) {
      const lastTourneyDate = keyToDate(presentKeys[presentKeys.length - 1]);
      if (lastTourneyDate > rangeEnd) rangeEnd = lastTourneyDate;
    }
  } else {
    // Nothing scheduled and no clock: no columns, rather than one invented day.
    if (presentKeys.length === 0) return { tournaments, days: [] };
    rangeStart = keyToDate(presentKeys[0]);
    rangeEnd = keyToDate(presentKeys[presentKeys.length - 1]);
  }
  const days: DayMeta[] = [];
  for (let dt = new Date(rangeStart); dt <= rangeEnd; dt.setDate(dt.getDate() + 1)) {
    days.push(formatDayMeta(new Date(dt)));
  }
  return { tournaments, days };
};

const compactCurrency = (amount: number): string => {
  if (!Number.isFinite(amount) || amount <= 0) return '';
  if (amount >= 1_000_000)
    return `$${(amount / 1_000_000).toFixed(amount % 1_000_000 === 0 ? 0 : 1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(amount % 1_000 === 0 ? 0 : 1)}K`;
  return `$${Math.round(amount)}`;
};

const formatHoursMinutes = (hours: number): string => {
  if (!Number.isFinite(hours) || hours <= 0) return '0H : 00M';
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}H : ${String(m).padStart(2, '0')}M`;
};

const adaptPromo = (rawPromo: any): PromoData | undefined => {
  if (!rawPromo) return undefined;
  const endDate = rawPromo.end_date ? new Date(rawPromo.end_date) : null;
  const weeklyHoursPlayed = Number(rawPromo?.hours?.breakdowns?.this_week ?? 0);
  const weeklyPct = Number(rawPromo?.hours?.progress?.weekly_percentage ?? 0);
  const totalPct = Number(rawPromo?.hours?.progress?.total_percentage ?? 0);
  const progress = Math.max(0, Math.min(100, weeklyPct || totalPct || 0));
  // TODO(product): reward.amount is currently missing/zero on test1 — falling
  // back to "$100K" per design until the promo configuration is fixed upstream.
  const rewardRaw = Number(rawPromo?.reward?.amount ?? 0);
  const rewardAmount = rewardRaw > 0 ? compactCurrency(rewardRaw) : '$100K';
  return {
    title: String(rawPromo.promo_name || 'Promotion'),
    rewardAmount,
    countdown: formatHoursMinutes(weeklyHoursPlayed),
    endsStamp: endDate
      ? `Ends ${endDate.getMonth() + 1}/${endDate.getDate()} — ${endDate
          .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
          .toUpperCase()}`
      : '',
    progressPercent: progress
  };
};

/**
 * Is the Activity Strip shown at all? EN-10638 held this at `false` until the
 * strip's numbers came from the CDN; EN-10417 (#3894) delivered that, so it is
 * on. See the note at its use below — this stays the one place to hide it.
 */
const ACTIVITY_STRIP_ENABLED = true;

const SaloonLobbyContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const texasCashGames = useAppSelector((state) => state.lobby.texasCashGames);
  const omahaCashGames = useAppSelector((state) => state.lobby.omahaCashGames);
  const sitAndGoList = useAppSelector((state: any) => state.lobby?.tournament?.sitAndGoList);
  const tournamentList = useAppSelector((state: any) => state.lobby?.tournament?.tournamentList);
  // Engine clock (epoch seconds) — authoritative "today" for the schedule.
  const serverTime = useAppSelector((state: any) => state.serverDetails?.serverTime || 0);
  const playerBankAmount = useAppSelector((state) => state.user.playerBankAmount);
  const playerChargeBalance = useAppSelector((state) => state.user.playerChargeBalance);
  const playerAvatar = useAppSelector((state) => state.user.playerAvatar);
  const recentTablesMeta = useAppSelector((state) => state.user.recentTables);
  const promotions = useAppSelector((state: any) => state.playerPromos?.promotions || []) as any[];
  const playerTournamentDetails = useAppSelector(
    (state: any) => state.lobby?.playerTournamentDetails || []
  ) as any[];

  // YourTables strip — derivation + persisted order shared with TableHeader.
  // navTables is what "Your Tables" shows: queued tables in their own slots.
  // activeTables (loaded tables only) still decides what is subscribed.
  const { activeTables, navTables, openTable: handleOpenTable } = useActiveTables();

  // EN-10064: advisory colluder restriction markers (kept by the
  // colluderRestrictions slice, fed by the REST load + `gotColluderRestrictions`
  // push). Per-game-type sets so tiles pick the right set by variant.
  const restrictedTexasCGIds = useAppSelector(selectRestrictedTexasCGIds);
  const restrictedOmahaCGIds = useAppSelector(selectRestrictedOmahaCGIds);
  const restrictedSngIds = useAppSelector(selectRestrictedSngIds);
  const restrictedMttIds = useAppSelector(selectRestrictedMttIds);

  const player: PlayerSummary = useMemo(
    () => ({
      cashBalance: Number(playerBankAmount) || 0,
      promoBalance: Number(playerChargeBalance) || 0,
      avatar: playerAvatar || ''
    }),
    [playerBankAmount, playerChargeBalance, playerAvatar]
  );

  const cashGames = useMemo<CashGame[]>(() => {
    const list = [...(texasCashGames || []), ...(omahaCashGames || [])];
    return list.map(adaptCashGame);
  }, [texasCashGames, omahaCashGames]);

  // Built before sitGos / tournaments so both can stamp each entry's
  // isRegistered field from the same source of truth.
  const registeredTournamentIds = useMemo(() => {
    const set = new Set<string>();
    for (const d of playerTournamentDetails || []) {
      if (d?.status === 'R' || d?.status === 'P') {
        const tid = d?.tournamentId;
        if (tid !== undefined && tid !== null) set.add(String(tid));
      }
    }
    return set;
  }, [playerTournamentDetails]);

  // Cancelled and finished sit & gos are dropped before adapting (isListedSitGo).
  const sitGos = useMemo<SitGo[]>(
    () =>
      (sitAndGoList || [])
        .filter(isListedSitGo)
        .map((raw: any, idx: number) => adaptSitGo(raw, idx, registeredTournamentIds)),
    [sitAndGoList, registeredTournamentIds]
  );

  // EN-11016: the published schedule plus every later occurrence of each
  // recurring tournament (recurringSchedule.ts). Off is the schedule as sent.
  // Projected rows live only here — never in the store — so nothing that acts
  // on a real tournament (registration, subscriptions, about-to-start) sees them.
  // Keyed on the minute, not the second `serverTime` ticks at, so the rows are
  // projected and adapted once a minute instead of every second. The cost is a
  // past occurrence lingering for under a minute — and by then the real row has
  // normally been rescheduled onto that day, which replaces the tile anyway.
  // The adapter reads the clock no finer than that: a start time to the minute,
  // the calendar's today to the day, and whether the first tick has landed —
  // so the first tick and a midnight rollover still land on time.
  const recurringScheduleEnabled = useFeatureFlag('tournamentRecurringSchedule');
  // Exposure is recorded where the player meets the decision: the Tournaments
  // tab on screen, which only SaloonLobby knows (it reports back via onFormatShown).
  const [shownFormat, setShownFormat] = useState<Format | null>(null);
  useFeatureFlagExposure(
    'tournamentRecurringSchedule',
    'tournament_calendar',
    shownFormat === 'tournament'
  );
  const serverMinute = Math.floor(serverTime / 60) * 60;
  const scheduleList = useMemo<any[]>(() => {
    const published = tournamentList || [];
    if (!recurringScheduleEnabled) return published;
    return [...published, ...projectRecurringTournaments(published, serverMinute)];
  }, [tournamentList, serverMinute, recurringScheduleEnabled]);

  const { tournaments, tournamentDays } = useMemo(() => {
    const { tournaments: ts, days } = adaptTournaments(
      scheduleList,
      registeredTournamentIds,
      serverMinute
    );
    return { tournaments: ts, tournamentDays: days };
  }, [scheduleList, registeredTournamentIds, serverMinute]);

  // Recently visited cash tables — Unity LobbiesView parity.
  //
  // Rules matching Unity (LobbiesView.cs lines 282-313):
  //   1. Tables not in the live lobby are dropped silently (no fallback tile).
  //   2. Sort: IsSubscribed DESC → SeatsTaken DESC.
  //      "IsSubscribed" = the player is currently active at that table
  //      (has an open WebSocket subscription via activeTables).
  //   3. No client-side count cap — the server already limits to 5 entries.
  //   4. Includes tables where the player is currently seated (YourTables and
  //      My Recent Tables intentionally overlap — different UX purposes).
  const recentItems = useMemo<Array<{ game: CashGame; recent: RecentTable }>>(() => {
    if (!recentTablesMeta.length) return [];
    const cashGameById = new Map<string, CashGame>(cashGames.map((g) => [g.id, g]));
    const subscribedIds = new Set(activeTables.map((t) => t.gameID));

    return (
      recentTablesMeta
        // Drop tables no longer in the live lobby (matches Unity's null-check behaviour).
        .filter((r) => cashGameById.has(r.gameID))
        .map((r) => ({ game: cashGameById.get(r.gameID)!, recent: r }))
        // Unity sort: IsSubscribed DESC (currently active tables first),
        // then SeatsTaken DESC (busiest tables next).
        .sort((a, b) => {
          const aSubscribed = subscribedIds.has(a.recent.gameID) ? 1 : 0;
          const bSubscribed = subscribedIds.has(b.recent.gameID) ? 1 : 0;
          if (bSubscribed !== aSubscribed) return bSubscribed - aSubscribed;
          return b.game.seated - a.game.seated;
        })
    );
  }, [recentTablesMeta, cashGames, activeTables]);

  const promo = useMemo<PromoData | undefined>(() => {
    const active = (promotions || []).find((p: any) => p?.is_active);
    return adaptPromo(active);
  }, [promotions]);

  // Platform-wide metrics polled from the activity-strip CDN document (see
  // useActivityStripFeed). `undefined` until the first successful read ever
  // lands (held in the store, which outlives a Lobby/GuestLobby
  // unmount+remount but not a page reload, so this also covers "left the
  // lobby before the first read, came back"). A fabricated { tablesActive: 0, playersSeated: 0,
  // inPlay: 0 } would be indistinguishable from a real zero-activity reading,
  // misleading players — but once a REAL reading has arrived, ActivityStrip
  // keeps showing it (flagged 'stale' via activityStatus below) rather than
  // reverting to no data, since a slightly-old real number is more useful
  // than a blank strip during a transient outage.
  const liveActivity = useAppSelector(
    (state: any) => state.activityStrip?.metrics
  ) as ActivityMetrics | null;
  const liveActivityUpdatedAt = useAppSelector((state: any) => state.activityStrip?.updatedAt) as
    | number
    | null;
  // The publisher's own cadence, as published in the CDN document's
  // maxAgeMs field — see reducers/activityStrip.tsx and deriveStaleAfterMs.
  const liveActivityMaxAgeMs = useAppSelector((state: any) => state.activityStrip?.maxAgeMs) as
    | number
    | null;
  // useActivityStripFeed's clockOffsetMs for the read that produced
  // liveActivityUpdatedAt — see that field's own comment. Added to this
  // component's own Date.now()-derived nowSec below before comparing against
  // liveActivityUpdatedAt (server truth), so a fast/slow device clock can't
  // flip isActivityStale in either direction.
  const liveActivityClockOffsetMs = (useAppSelector(
    (state: any) => state.activityStrip?.clockOffsetMs
  ) ?? 0) as number;

  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);

  const selectedTournament = useMemo(() => {
    if (!selectedTournamentId) return null;
    return (
      scheduleList.find((t: any) => String(t?.tournamentId ?? t?.id) === selectedTournamentId) ||
      (sitAndGoList || []).find(
        (s: any) => String(s?.tournamentId ?? s?.id) === selectedTournamentId
      ) ||
      null
    );
  }, [selectedTournamentId, scheduleList, sitAndGoList]);

  const openTournament = useCallback((id: string) => setSelectedTournamentId(id), []);
  const closeTournament = useCallback(() => setSelectedTournamentId(null), []);

  // "About to start" notification — Unity's LobbiesModel parity.
  //
  // Ticks `nowSec` every 5s so the in-lobby detection effect re-runs without
  // re-rendering the whole tree every second; the modal itself ticks every
  // 1s once it's open.
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = window.setInterval(() => setNowSec(Math.floor(Date.now() / 1000)), 5000);
    return () => window.clearInterval(id);
  }, []);

  // Staleness check for the activity strip, reusing this same 5s ticker
  // rather than comparing against Date.now() directly in render. `nowSec`
  // only advances once every 5s, so this can flip at most that often — a
  // controlled cadence, not one that flickers with unrelated re-renders
  // elsewhere in this component hitting different wall-clock instants.
  // `updatedAt` survives a Lobby/GuestLobby remount (redux, not local hook
  // state), so this also catches "left the lobby, CDN went down, came back" —
  // not just an outage that started while already on this screen. The
  // threshold itself is derived from the document's own published
  // maxAgeMs (deriveStaleAfterMs) rather than a fixed constant, so a
  // publisher-side cadence change takes effect here too without a client
  // rebuild.
  const isActivityStale = useMemo(
    () =>
      liveActivityUpdatedAt !== null &&
      nowSec * 1000 + liveActivityClockOffsetMs - liveActivityUpdatedAt >
        deriveStaleAfterMs(liveActivityMaxAgeMs),
    [liveActivityUpdatedAt, liveActivityMaxAgeMs, liveActivityClockOffsetMs, nowSec]
  );

  // 'loading' is only ever meant to describe the moment right after mount,
  // before the CDN's first response has had a chance to arrive — it has no
  // exit if the CDN is unreachable from the very start (global-data-cdn not
  // yet deployed to this stage, a CORS/network misconfiguration, the "CDN
  // Resource Ownership" gap the design doc's Deployment section warns some
  // stages may hit). Without a bound, that reads as "Loading…" forever, with
  // no error state and no action for the player — exactly the dead end the
  // ux-accessibility overlay's state-coverage rule forbids. Bounded by the
  // same deriveStaleAfterMs threshold the staleness check above already uses
  // (the document's own published cadence, or its 5000ms default) rather
  // than inventing a second magic constant, and measured off the same
  // 5s-ticking nowSec clock for the same "controlled cadence, not render
  // jitter" reason isActivityStale is.
  const mountedAtMsRef = useRef(Date.now());
  const isActivityUnavailable = useMemo(
    () => !liveActivity && nowSec * 1000 - mountedAtMsRef.current > deriveStaleAfterMs(undefined),
    [liveActivity, nowSec]
  );

  // Unlike the old "hide once stale" behaviour, the last good reading is
  // still passed through when stale — SaloonLobby/ActivityStrip render it
  // flagged as 'stale' rather than making it disappear, per the defensive-
  // programming/UX requirement that a degraded feed show an explicit status
  // instead of silently reverting to no data. The true "never loaded" case
  // (liveActivity still null) yields 'loading' briefly, then 'unavailable'
  // once isActivityUnavailable's bound trips — useActivityStripFeed keeps
  // polling with backoff regardless, so this flips back to 'fresh' on its
  // own the moment a real reading lands; nothing here disables the feed.
  const activity = liveActivity ?? undefined;
  const activityStatus: ActivityStripStatus = !liveActivity
    ? isActivityUnavailable
      ? 'unavailable'
      : 'loading'
    : isActivityStale
    ? 'stale'
    : 'fresh';

  // Dispatched by ActivityStrip's Retry action (only rendered while
  // activityStatus is 'unavailable'). This component is already connected to
  // redux (the useAppSelector reads above), so dispatching here — rather than
  // threading a callback down from wherever useActivityStripDispatch is
  // mounted (Lobby.tsx/GuestLobby.tsx, several layers above this one) —
  // avoids prop-drilling a function through every intermediate layer.
  // useActivityStripDispatch reads the resulting retryToken back out of this
  // same slice and forwards it to useActivityStripFeed as retrySignal, whose
  // effect actually restarts the poll.
  const onActivityRetry = useCallback(() => {
    dispatch(requestActivityStripRetry());
  }, [dispatch]);

  // Mirrors Unity's `GameAboutToStart` set — once shown for a tournament
  // (joined, cancelled, or dismissed), don't pop again this lobby mount.
  const aboutToStartFired = useRef<Set<string>>(new Set());
  const [aboutToStartTournament, setAboutToStartTournament] = useState<any | null>(null);

  const registeredTournaments = useMemo(
    () => findRegisteredTournaments(playerTournamentDetails, tournamentList, sitAndGoList),
    [playerTournamentDetails, tournamentList, sitAndGoList]
  );

  useEffect(() => {
    if (aboutToStartTournament) return;
    const winner = pickAboutToStartCandidate(
      registeredTournaments,
      nowSec,
      aboutToStartFired.current
    );
    if (winner) {
      const id = String(winner.tournamentId || winner.id || '');
      aboutToStartFired.current.add(id);
      setAboutToStartTournament(winner);
    }
  }, [registeredTournaments, nowSec, aboutToStartTournament]);

  // Re-pick the live tournament object so the modal's countdown / status
  // stay accurate as Redux pushes updates.
  const liveAboutToStartTournament = useMemo(() => {
    if (!aboutToStartTournament) return null;
    const id = aboutToStartTournament.tournamentId || aboutToStartTournament.id;
    return (
      [...(tournamentList || []), ...(sitAndGoList || [])].find(
        (t: any) => (t.tournamentId || t.id) === id
      ) || aboutToStartTournament
    );
  }, [aboutToStartTournament, tournamentList, sitAndGoList]);

  // Auto-dismiss when the tournament reaches a terminal state — neither the
  // "about to start" nor "has started" framings make sense once Finished /
  // Cancelled, and we don't want a stale modal sitting on the lobby.
  useEffect(() => {
    if (!liveAboutToStartTournament) return;
    const status = liveAboutToStartTournament.status;
    if (status === 'F' || status === 'C') {
      setAboutToStartTournament(null);
    }
  }, [liveAboutToStartTournament]);

  const handleAboutToStartClose = useCallback(() => setAboutToStartTournament(null), []);
  const handleAboutToStartJoin = useCallback(() => {
    const t = liveAboutToStartTournament;
    setAboutToStartTournament(null);
    if (!t) return;
    // Direct-to-table: SNG (tournament IS the table) or MTT with an assigned
    // seat → open the felt straight from here. Matches the flow the Join Table
    // button in TournamentDetailModal takes so the player skips the detail-modal
    // hop when the seat is known. resolveAboutToStartJoinTarget is a pure fn —
    // called once per click, no per-render cost.
    const target = resolveAboutToStartJoinTarget(t, playerTournamentDetails);
    if (target) {
      handleOpenTable(target.gameID, target.game, 'no');
      return;
    }
    // Fallback: MTT with no seat assigned yet (gameID -1). Open the tournament
    // detail modal so "Finding the best seat for you…" can route the player
    // automatically once the engine assigns one.
    const id = String(t.tournamentId || t.id || '');
    if (id) openTournament(id);
  }, [liveAboutToStartTournament, playerTournamentDetails, handleOpenTable, openTournament]);

  // Guest (not-logged-in) mode: guests can OBSERVE. A table click opens the
  // table in observe mode (no seat); a tournament click opens its detail. Live
  // state arrives over the serverless observe path; seat/register actions stay
  // gated (handled inside the table/tournament views).
  const guest = useGuest();
  // Lobby Lab (dev only — NEUTRAL in production, see lobbyLabState): fixture
  // overrides for states a dev server cannot reach, and the EN-10638 gate on
  // the Activity Strip.
  const lab = useLobbyLab();

  // Lobby Lab "demo tables": production's tournament calendar instead of this
  // environment's one or two hand-made events (see projectTournaments). Keyed on
  // the DAY, not on serverTime itself — that ticks every second, and rebuilding
  // fifty tournaments a second would re-render the tab for nothing.
  const demoDayKey = useMemo(() => {
    const now = serverTime > 0 ? new Date(serverTime * 1000) : new Date();
    return `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
  }, [serverTime]);
  const demoSchedule = useMemo(
    () => (lab.demoTables ? projectTournaments(serverTime, tournamentBannerFor) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lab.demoTables, demoDayKey]
  );
  const tournamentsForView = demoSchedule?.tournaments ?? tournaments;
  const tournamentDaysForView = demoSchedule?.days ?? tournamentDays;
  const { openTable: navigateToTable } = useContext(ViewManagerContext);
  const onOpenTable = guest
    ? (gameID: string, game: string) => navigateToTable(gameID, game, 'yes')
    : handleOpenTable;
  const onOpenTournament = openTournament;

  // A guest's "Login/Sign up to Register" click (TournamentDetailModal)
  // stashes the tournament ID and sends them to login. Unlike a table URL,
  // the modal isn't reflected in the route, so landing back on the lobby
  // isn't enough on its own — re-open the modal for that tournament and flag
  // it to resume straight to the register confirm step. Retried on every
  // tournamentList/sitAndGoList update since the lobby's live data may not
  // have arrived yet on the first render after the post-login reload.
  const [autoRegisterTournamentId, setAutoRegisterTournamentId] = useState<string | null>(null);
  // A fresh page starts on Cash; table returns preserve the session's tab.
  // The post-login intent overrides either: otherwise the modal opens for the right
  // tournament while the lobby underneath still shows Cash. Set once we know
  // which list actually matched (tournamentList → MTT, sitAndGoList → SNG),
  // consumed by SaloonLobby the same way autoRegisterTournamentId is.
  // EN-11109: leaving a finished Sit & Go arms a one-shot landing on the Sit &
  // Go tab before the lobby mounts (see lobbyLanding). Peeked, not consumed, in
  // the initializer (StrictMode runs it twice); cleared once mounted.
  const [resumeFormat, setResumeFormat] = useState<Format | null>(() => peekLobbyLanding());
  useEffect(() => {
    clearLobbyLanding();
  }, []);
  useEffect(() => {
    if (guest) return;
    const pendingId = peekTournamentIntent();
    if (!pendingId) return;
    const isMtt = (tournamentList || []).some(
      (t: any) => String(t?.tournamentId ?? t?.id) === pendingId
    );
    const isSng =
      !isMtt &&
      (sitAndGoList || []).some((t: any) => String(t?.tournamentId ?? t?.id) === pendingId);
    if (!isMtt && !isSng) return;
    clearTournamentIntent();
    setSelectedTournamentId(pendingId);
    setAutoRegisterTournamentId(pendingId);
    setResumeFormat(isMtt ? 'tournament' : 'sitgo');
  }, [guest, tournamentList, sitAndGoList]);

  // Lobby Lab: show production's stake ladder, nine tables to a stake, for a
  // demo (demoTables.ts). Live tables keep their place; demo ids are refused
  // below so a stray click during a demo cannot try to open a table the engine
  // has never heard of.
  const cashGamesForView = useMemo(() => {
    let list = cashGames;
    if (lab.demoTables) list = padCashGames(list);
    if (lab.highTraffic) list = applyHighTraffic(list);
    return list;
  }, [lab.demoTables, lab.highTraffic, cashGames]);
  // Sit & Go tab, same switch: production's prize-pool ladder instead of the
  // environment's seeded rows (demoTables.ts) — and the heads-up ladder beside
  // it only while the tab's seat toggle is on to reach it (EN-10747).
  const sitgoSeatToggle = useFeatureFlag('sitgoSeatToggle');
  const sitGosForView = useMemo(
    () =>
      lab.demoTables
        ? projectSitGos(sitGos, {
            headsUp: sitgoSeatToggle,
            cheapestTables: lab.sitgoCheapestTables
          })
        : sitGos,
    [lab.demoTables, lab.sitgoCheapestTables, sitgoSeatToggle, sitGos]
  );
  const openTournamentUnlessDemo = useCallback(
    (id: string) => {
      if (isDemoTableId(id)) return;
      onOpenTournament(id);
    },
    [onOpenTournament]
  );
  const openTableUnlessDemo = useCallback(
    (gameID: string, game: string): boolean => {
      if (isDemoTableId(gameID)) return false;
      return onOpenTable(gameID, game);
    },
    [onOpenTable]
  );

  /**
   * EN-10638 hid the Activity Strip in every build while its numbers were a
   * client-side estimate that did not track the live data (owner, 2026-09-10).
   * EN-10417 (#3894, 2026-09-17) moved the numbers to the CDN activity document
   * — the condition this flag was waiting on — and the 2026-09-16 release plan
   * sequences that release ahead of this lobby with the strip on both tabs, so
   * the flag is ON.
   *
   * It stays the ONE PLACE to hide the strip again: set it to `false` and the
   * strip goes — metrics AND status together, because a strip with a status
   * and no metrics is its "Loading…" placeholder, not an absence. Everything
   * else stays live and tested: ActivityStrip itself, its collapsing row under
   * the compact header, the pin chain beneath it, and the filter bar, which
   * reads whether the strip is present and re-centres itself either way.
   *
   * LOCAL PREVIEW (owner, 2026-09-11, for a design demo): while the flag is
   * off, the Lobby Lab's switch can still show the strip on the dev server —
   * and only there: CRA sets NODE_ENV to 'development' for `yarn start` and to
   * 'production' for every build, so a deployed lobby ignores the switch
   * whatever its stored state says.
   */
  const activityStripShown =
    ACTIVITY_STRIP_ENABLED || (process.env.NODE_ENV === 'development' && lab.activityStrip);
  const activityForView = activityStripShown ? lab.overrides.activity ?? activity : undefined;
  // A Lab fixture IS the reading, so it is fresh by definition: the feed's
  // loading / stale / unavailable states describe a document the fixture has
  // replaced.
  // The maintenance marquee's Lab preview: sample copy, and only that — the
  // lobby's maintenance behaviour reads the store (see isMaintenance above), so
  // nothing dims and every table keeps taking clicks behind it.
  const maintenancePreview = lab.maintenanceMarquee ? LAB_MAINTENANCE_NOTICE : undefined;
  const activityStatusForView: ActivityStripStatus | undefined = !activityStripShown
    ? undefined
    : lab.overrides.activity
    ? 'fresh'
    : activityStatus;

  return (
    <GuestOverrideProvider value={lab.member ? false : null}>
      <SaloonLobby
        player={lab.overrides.player ?? player}
        activity={activityForView}
        activityStatus={activityStatusForView}
        maintenancePreview={maintenancePreview}
        onActivityRetry={onActivityRetry}
        promo={promo}
        cashGames={cashGamesForView}
        sitGos={sitGosForView}
        tournaments={tournamentsForView}
        tournamentDays={tournamentDaysForView}
        serverTime={serverTime}
        activeTables={lab.overrides.activeTables ?? navTables}
        recentItems={lab.overrides.recentItems ?? recentItems}
        restrictedTexasCGIds={restrictedTexasCGIds}
        restrictedOmahaCGIds={restrictedOmahaCGIds}
        restrictedSngIds={restrictedSngIds}
        restrictedMttIds={restrictedMttIds}
        onOpenTable={openTableUnlessDemo}
        onOpenTournament={openTournamentUnlessDemo}
        forceFormat={resumeFormat}
        onForceFormatConsumed={() => setResumeFormat(null)}
        onFormatShown={setShownFormat}
        newTabsPreview={lab.headsUpNewBadge ? SITGO_NEW_PREVIEW : undefined}
      />
      {/* Begin with the lobby feed, even before the SnG tab/tile is visible.
          The shared payout store bounds requests and serves both modals. */}
      {sitGos
        .filter((game) => !restrictedSngIds.has(game.id))
        .map((game) => (
          <PayoutPreload
            key={game.id}
            id={Number(game.id)}
            keepFresh={false}
            revision={`${game.registered}:${game.capacity}:${game.buyIn}:${game.prizePool}`}
          />
        ))}
      <TournamentDetailModal
        open={selectedTournament !== null}
        onClose={closeTournament}
        tournament={selectedTournament}
        autoRegisterOnOpen={
          autoRegisterTournamentId !== null && autoRegisterTournamentId === selectedTournamentId
        }
        onAutoRegisterConsumed={() => setAutoRegisterTournamentId(null)}
      />
      <TournamentAboutToStartModal
        open={liveAboutToStartTournament !== null}
        tournament={liveAboutToStartTournament}
        onClose={handleAboutToStartClose}
        onJoin={handleAboutToStartJoin}
      />
    </GuestOverrideProvider>
  );
};

export default SaloonLobbyContainer;
